import React from 'react';
import { Card, CardContent } from './ui/card';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { Button } from './ui/button';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { useLanguage } from '../context/LanguageContext';

const Contact = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [regionalRef, regionalVisible] = useScrollAnimation();
  const [internationalRef, internationalVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-blue-50/30 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 animate-on-scroll ${headerVisible ? 'animate-visible' : ''}`}
        >
          <h2 className="headline-font text-4xl md:text-5xl text-slate-900 mb-4">{t('contact.title')}</h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Regional Head Office - Amman */}
            <div
              ref={regionalRef}
              className={`animate-on-scroll-left ${regionalVisible ? 'animate-visible' : ''}`}
            >
              <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300 h-full">
                <CardContent className="p-8">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <MapPin className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="subheadline-font text-xl text-slate-900 mb-6">{t('contact.regionalOffice')}</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.address')}</p>
                        <p className="text-gray-700 font-medium">{t('contact.ammanLocation')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.phone')}</p>
                        <a
                          href="tel:+962799172424"
                          className="text-blue-700 hover:text-blue-900 font-medium transition-colors duration-200"
                        >
                          +962 799 172 424
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.email')}</p>
                        <a
                          href="mailto:info@aimtradejo.com"
                          className="text-blue-700 hover:text-blue-900 font-medium transition-colors duration-200"
                        >
                          info@aimtradejo.com
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* International Office - Munich */}
            <div
              ref={internationalRef}
              className={`animate-on-scroll-right ${internationalVisible ? 'animate-visible' : ''}`}
            >
              <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300 h-full">
                <CardContent className="p-8">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Globe className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="subheadline-font text-xl text-slate-900 mb-6">{t('contact.internationalOffice')}</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.address')}</p>
                        <p className="text-gray-700 font-medium">{t('contact.munichLocation')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.phone')}</p>
                        <a
                          href="tel:+4917663106693"
                          className="text-blue-700 hover:text-blue-900 font-medium transition-colors duration-200"
                        >
                          +49 176 631 066 93
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{t('contact.email')}</p>
                        <a
                          href="mailto:international@aimtradejo.com"
                          className="text-blue-700 hover:text-blue-900 font-medium transition-colors duration-200"
                        >
                          international@aimtradejo.com
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Section */}
          <div 
            ref={ctaRef}
            className={`mt-12 text-center bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-12 shadow-xl animate-on-scroll-scale ${ctaVisible ? 'animate-visible' : ''}`}
          >
            <h3 className="subheadline-font text-3xl text-white mb-4">{t('contact.ctaTitle')}</h3>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              {t('contact.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-blue-500 text-slate-900 hover:bg-blue-400 font-semibold px-8 transition-colors duration-200"
                onClick={() => window.location.href = 'mailto:info@aimtradejo.com'}
              >
                <Mail className="me-2 w-5 h-5" />
                {t('contact.ctaEmail')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900 font-semibold px-8 transition-all duration-200"
                onClick={() => window.location.href = 'tel:+962799172424'}
              >
                <Phone className="me-2 w-5 h-5" />
                {t('contact.ctaPhone')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
